import { Component, Element, Event, EventEmitter, Listen, Prop, State, Watch, h } from '@stencil/core';

export interface IOptionsSelect {
  label: string;
  isChecked: boolean;
  key: string;
  nameAvatar?: string;
  imgSRCAvatar?: string;
}

@Component({
  tag: 'lab-ds-select',
  styleUrl: 'lab-ds-select.css',
  shadow: true,
})
export class LabDsSelect {
  @Prop() options!: Array<IOptionsSelect>;

  @Prop() optionsSelected: Array<IOptionsSelect>;

  @State() optionsState: Array<IOptionsSelect> = this.options;

  @State() optionsFiltered: Array<IOptionsSelect> = this.optionsState;

  @State() isOptionsVisible: boolean = false;

  @Prop() loading: boolean = false;

  @Prop() type: 'checkbox' | 'radioButton' = 'checkbox';

  @Prop() avatar: boolean = true;

  @State() value: string;

  @Prop() titleInput: string;

  @Prop() label: string = '';

  @Prop() placeholderSearch: string = 'Digite sua busca';

  @Prop() searcheableWithQuery: boolean = false;

  @Prop() isSearchable: boolean = true;

  @Prop() disabled: boolean = false;

  @Prop() helperText: string;

  @Prop() state: 'error' | 'default' = 'default';

  @Prop() size: 'small' | 'medium' = 'small';

  @Event() finalLineNextPage: EventEmitter<boolean>;

  @Event() changeInputOptions: EventEmitter<Array<{ label: string; isChecked: boolean; key: string }>>;

  @Event() changeInputSearch: EventEmitter<string>;

  handleSelect() {
    if (this.disabled) return;

    this.isOptionsVisible = !this.isOptionsVisible;
  }

  handleSelectOption(event) {
    if (this.type === 'radioButton') {
      this.optionsState.forEach(option => {
        if (option.key === event) {
          option.isChecked = true;
        } else {
          option.isChecked = false;
        }
      });
      this.isOptionsVisible = false;
    }

    if (this.type === 'checkbox') {
      this.optionsState = this.optionsState.map(option => ({ ...option, isChecked: option.key === event ? !option.isChecked : option.isChecked }));
    }

    this.optionsSelected = this.optionsState.filter(option => option.isChecked);

    this.optionsFiltered = this.optionsState;

    this.value = '';

    this.changeInputOptions.emit(this.optionsSelected);
  }

  handleRemoveOption(event) {
    this.optionsSelected = this.optionsSelected.filter(option => option.key !== event);

    this.optionsState.forEach(option => {
      if (option.key === event) {
        option.isChecked = false;
      }
    });

    this.optionsFiltered = this.optionsState;

    this.changeInputOptions.emit(this.optionsSelected);
  }

  handleSearch({ detail }) {
    if (this.searcheableWithQuery) {
      this.changeInputSearch.emit(detail);
      return;
    }

    this.optionsFiltered = this.optionsState.filter(option =>
      option.label
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .includes(
          detail
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, ''),
        ),
    );
  }

  @Watch('options')
  watchOptions() {
    this.optionsState = this.options;
    this.optionsFiltered = this.options;
    this.optionsSelected = this.options.filter(option => option.isChecked);
  }

  // eslint-disable-next-line @stencil-community/element-type
  @Element() el: HTMLElement;

  @Listen('click', { target: 'window' })
  checkForClickOutside(ev) {
    if (this.el.contains(ev.target)) {
      return;
    }
    this.isOptionsVisible = false;
  }

  componentDidUpdate() {
    const scroll: HTMLElement = this.el.shadowRoot.querySelector('.hints');
    if (scroll) {
      scroll.addEventListener(
        'scroll',
        () => {
          if (Math.abs(scroll.scrollHeight - scroll.clientHeight - scroll.scrollTop) < 1) {
            this.finalLineNextPage.emit(true);
          }
        },
        {
          passive: true,
        },
      );
    }
  }

  render() {
    const isOptionsVisibleIcon = this.isOptionsVisible ? 'expand_less' : 'expand_more';

    const slider: HTMLElement = this.el.shadowRoot.querySelector('.chip-selected');
    let isDown = false;
    let startX;
    let scrollLeft;

    if (slider) {
      slider.addEventListener('mousedown', (e: MouseEvent) => {
        isDown = true;
        slider.classList.add('active');
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      });
      slider.addEventListener('mouseleave', () => {
        isDown = false;
        slider.classList.remove('active');
      });
      slider.addEventListener('mouseup', () => {
        isDown = false;
        slider.classList.remove('active');
      });
      slider.addEventListener('mousemove', (e: MouseEvent) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX) * 3; //scroll-fast
        slider.scrollLeft = scrollLeft - walk;
      });
    }

    return (
      <div class={`v4-input ${this.state} ${this.size} ${this.disabled && 'disabled'} ${this.optionsSelected?.length > 0 && this.type === 'checkbox' ? 'chip-select' : ''}`}>
        <div class="input-title-container">
          {this.titleInput && <span class="title-text">{this.titleInput}</span>}
          <div
            onClick={event => {
              event.stopPropagation();
              this.handleSelect();
            }}
            class={`input-icon ${this.isOptionsVisible ? 'active' : ''} `}
          >
            <span class="input-field">
              <div class="chip-selected">
                {this.optionsSelected?.length > 0 && this.type === 'checkbox'
                  ? this.optionsSelected.map(option => (
                      <lab-ds-chip
                        size="small"
                        class="chip"
                        avatar={this.avatar}
                        nameAvatar={option.nameAvatar}
                        imageSRCAvatar={option.imgSRCAvatar}
                        label={option.label}
                        onHandleCloseChip={() => this.handleRemoveOption(option.key)}
                      />
                    ))
                  : this.optionsSelected?.map(option => option.label)}
                {!(this.optionsSelected?.length > 0) && <span class="select-placeholder">{this.label}</span>}
              </div>
            </span>

            {this.state === 'error' && <lab-ds-icon-not-selectable class="icon error" size="small" icon="error" />}

            <lab-ds-icon-not-selectable class={`icon right`} size="small" icon={isOptionsVisibleIcon} />
          </div>
        </div>
        {this.isOptionsVisible && (
          <div class="options">
            <div class="hints">
              <div class="hints-container">
                {this.isSearchable && (
                  <lab-ds-search-bar
                    class="search-bar"
                    value={this.value}
                    onChangeInput={event => {
                      this.handleSearch(event);
                    }}
                    label={this.placeholderSearch}
                  />
                )}
                {this.optionsFiltered?.length > 0 &&
                  this.optionsFiltered?.map(hint => {
                    return (
                      <div key={hint.key} class="option">
                        {this.type === 'checkbox' ? (
                          <lab-ds-checkbox checked={hint.isChecked} onHandleCheckbox={() => this.handleSelectOption(hint.key)} avatar={this.avatar} label={hint.label} />
                        ) : (
                          <lab-ds-radio-button checked={hint.isChecked} onClick={() => this.handleSelectOption(hint.key)} avatar={this.avatar} label={hint.label} />
                        )}
                      </div>
                    );
                  })}
                {this.loading && <lab-ds-skeleton />}
              </div>
            </div>
          </div>
        )}
        {this.helperText && <span class="helper-text">{this.helperText}</span>}
      </div>
    );
  }
}
